import * as React from 'react';
import { useSelector } from 'react-redux';
import { sortBy } from '@debbie/common/dist/economy/helpers/utils.js';
import { TenantStatus } from '@debbie/common/dist/tenants/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { selectAuthToken } from '@debbie/cortex/dist/auth/selectors.js';
import { setSelectedCreditorId, setSelectedTenantIds, } from '@debbie/cortex/dist/internal.js';
import { selectTenantId } from '@debbie/cortex/dist/selectedOwners/selectors.js';
import { CASEWORKER_URL, DEFAULT_CREDITOR_WEB } from '../../config.js';
import { LogOut } from '../Icons/index.js';
import Input from '../Input.js';
import CreditorOrTenant from '../CreditorOrTenant.js';
import TenantStatusBadge from '../TenantStatusBadge.js';
import { useAccessCreditors } from '@debbie/cortex/dist/creditors/index.js';
const tenantStatusOrder = {
    ['ACTIVE']: 1,
    ['GETTING_STARTED']: 2,
    ['DEMO']: 3,
    ['SANDBOX']: 4,
    ['DISABLED']: 5,
    ['creditor']: 6,
};
const filter = (accessCreditors) => {
    const isSystemStandardDomain = (DEFAULT_CREDITOR_WEB || '').includes(window.location.hostname) ||
        (CASEWORKER_URL || '').includes(window.location.hostname);
    if (isSystemStandardDomain) {
        return accessCreditors;
    }
    else {
        return accessCreditors.filter((creditor) => {
            return creditor.url.includes(window.location.hostname);
        });
    }
};
const SelectableEntities = (props) => {
    const selectedTenantId = useSelector(selectTenantId);
    const selectedCreditorId = useSelector((s) => s.selectedCreditor);
    const lang = useSelector((s) => s.lang);
    const authToken = useSelector(selectAuthToken);
    const creditorsHavingAccessToLoadable = useAccessCreditors();
    const _creditorsHavingAccessTo = h.hasData(creditorsHavingAccessToLoadable)
        ? creditorsHavingAccessToLoadable.data
        : null;
    const creditorsHavingAccessTo = _creditorsHavingAccessTo
        ? filter(_creditorsHavingAccessTo)
        : null;
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedIndex, setSelectedIndex] = React.useState(null);
    const entities = props.tenants
        .filter((tenant) => 
    // Only redirect to tenants that the user has "caseworking" access to
    ['super-admin', 'admin', 'caseworker'].includes(tenant.role))
        .map((tenant) => {
        let badge = undefined;
        if ([
            TenantStatus.Disabled,
            TenantStatus.Demo,
            TenantStatus.Sandbox,
            TenantStatus.GettingStarted,
        ].includes(tenant.status)) {
            badge = (React.createElement(TenantStatusBadge, { status: tenant.status, style: { float: 'right' } }));
        }
        return {
            type: 'tenant',
            groupByKey: tenant.status,
            id: tenant.id,
            details: tenant,
            active: tenant.id === selectedTenantId,
            link: `${CASEWORKER_URL}?tenantId=${tenant.id}&authToken=${authToken}`,
            name: tenant.name,
            tooltip: tenant.name +
                (tenant.status !== TenantStatus.Active
                    ? ' [' + lang.tenantStatus[tenant.status] + ']'
                    : ''),
            badge,
            description: (React.createElement(React.Fragment, null,
                React.createElement("b", null, tenant.countryCode),
                " ",
                tenant.name)),
        };
    })
        .concat((creditorsHavingAccessTo || []).map((creditor) => ({
        type: 'creditor',
        groupByKey: 'creditor',
        id: creditor.creditorId,
        details: creditor,
        active: creditor.creditorId === selectedCreditorId,
        link: `${creditor.url}/?creditorId=${creditor.creditorId}&tenantId=${creditor.tenantId}&authToken=${authToken}`,
        tooltip: creditor.name,
        name: creditor.name,
        description: React.createElement(React.Fragment, null, creditor.id),
    })))
        .sort(sortBy((a, b) => tenantStatusOrder[a.groupByKey] - tenantStatusOrder[b.groupByKey], (a, b) => a.name.localeCompare(b.name)));
    const filteredEntities = (searchQuery
        ? entities.filter((entity) => {
            const searchable = entity.name + entity.id;
            return (searchable
                .toLocaleLowerCase()
                .indexOf(searchQuery.toLocaleLowerCase()) !== -1);
        })
        : entities).map((entity, i) => ({
        ...entity,
        selected: selectedIndex === i,
    }));
    const groupedEntities = filteredEntities.reduce((acc, entity) => {
        if (!acc[entity.groupByKey]) {
            acc[entity.groupByKey] = [];
        }
        acc[entity.groupByKey] = acc[entity.groupByKey].concat(entity);
        return acc;
    }, {});
    const select = (entity) => {
        setSelectedCreditorId(null);
        setSelectedTenantIds(null);
        location.replace(entity.link);
    };
    return (React.createElement(React.Fragment, null,
        props.tenants.length > 2 && (React.createElement(Input, { placeholder: lang.misc.search, value: searchQuery, onChange: (e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
            }, autofocus: true, onFocus: () => setSelectedIndex(0), onKeyDown: (e) => {
                switch (e.key) {
                    case 'ArrowUp': {
                        const newIndex = (selectedIndex || 0) - 1;
                        setSelectedIndex(newIndex < 0 ? filteredEntities.length - 1 : newIndex);
                        break;
                    }
                    case 'ArrowDown': {
                        const newIndex = (selectedIndex || 0) + 1;
                        setSelectedIndex(newIndex >= filteredEntities.length ? 0 : newIndex);
                        break;
                    }
                    case 'Enter': {
                        const selectedEntity = filteredEntities.find((entity) => entity.selected);
                        if (selectedEntity) {
                            select(selectedEntity);
                        }
                        break;
                    }
                }
            } })),
        Object.entries(groupedEntities).map(([type, entities]) => {
            return (React.createElement("div", { key: type },
                props.tenants.length > 3 && (React.createElement("h4", null, type === 'creditor'
                    ? lang.rightNav.creditors
                    : lang.tenantStatusPlural[type])),
                React.createElement("div", null, entities.map((entity) => {
                    return (React.createElement(CreditorOrTenant, { key: entity.id, active: entity.active, onClick: () => select(entity), selected: entity.selected, name: entity.name, description: entity.description, badge: entity.badge }));
                }))));
        }),
        React.createElement("p", { onClick: () => {
                c.auth.logout();
            }, style: {
                marginTop: '1.5em',
                cursor: 'pointer',
                textAlign: 'center',
            } },
            React.createElement(LogOut, { style: { margin: '-0.1em 0' } }),
            " ",
            lang.auth.logout)));
};
export default SelectableEntities;
//# sourceMappingURL=SelectableEntities.js.map